import React, { useMemo, useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import CalendarComponent from './CalenderComponent';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import CreateEventModal from './CreateEventModal';
import PendingApprovals from './PendingApprovals';
import useAxiosInstance from '../../lib/useAxiosInstance';
import { useQuery } from 'react-query';
import { toast, ToastContainer } from 'react-toastify';
import EventDetailsModal from './EventDetailsModal';
import { Button, Modal, DatePicker } from 'antd';
import "../../Styles/eventScheduler.css"

const Dashboard = () => {
  const themes=localStorage.getItem("themes")
  const axiosInstance = useAxiosInstance();
  const user = JSON.parse(localStorage.getItem("user"));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // const [showCalendarView, setShowCalendarView] = useState( false);
  const [showCalendarView, setShowCalendarView] = useState(
    user?.role === "data_entry_operator"
  );    
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [createEventTab, setCreateEventTab] = useState('visit'); 
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [calendarPrompt, setCalendarPrompt] = useState({
    visible: false,
    eventId: null,
    eventData: null,
  });

  const {
  data: dndEntries = [],
  refetch: refetchDND
} = useQuery('DND-List', async () => {
  const res = await axiosInstance.get('api/eventScheduler/getDND');
  return res?.data?.data || [];
});

  const fetchEvents = async () => {
    const response = await axiosInstance.get('api/eventScheduler/events');
    return response.data;
  };

  const {
    data,
    isLoading,
    error,
    isSuccess,
    refetch,
  } = useQuery(["Event-List"], fetchEvents, {
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const events = useMemo(() => {
    if (!data?.status || !data?.data?.events) return [];
    return data.data.events;
  }, [data]);

  const handleCreateEvent = (newEvent) => {
    setIsCreateModalOpen(false);
  };

  const handleDateSelect = (date, startTime, endTime) => {
    setSelectedDate(date);
    setSelectedTime(startTime || '');
    setSelectedEndTime(endTime || '');

    if (startTime || endTime) {
      setIsCreateModalOpen(true);
    }
  };

  const handleSidebarCalendarClick = (date, view) => {
    const formattedDate = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    setShowCalendarView(true);
    setCalendarView(view);
    setSelectedDate(formattedDate);
  };

  const openCreateEventModalWithTab = (tabName) => {
    setCreateEventTab(tabName);
    setIsCreateModalOpen(true);
  };

  const openEventModal = (eventId) => {
    setSelectedEventId(eventId);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setSelectedEventId(null);
    setIsEventModalOpen(false);
  };

  const confirmCalendarSync = async () => {
    const { eventData } = calendarPrompt;

    try {
      const approver = user;
      const attendeesEmails = eventData.attendees?.map(a => a.email).filter(Boolean) || [];

      await axiosInstance.post('api/eventScheduler/add-event-google', {
        eventId: eventData._id,
        title: eventData.eventTitle,
        description: eventData.description,
        start: eventData.start,
        end: eventData.end,
        attendees: attendeesEmails,
        organizerEmail: eventData?.email,
      });

      toast.success("Added to Google Calendar");
    } catch (err) {
      toast.error("Failed to sync with Google Calendar");
    } finally {
      setCalendarPrompt({ visible: false, eventId: null, eventData: null });
    }
  };

  const cancelCalendarSync = () => {
    setCalendarPrompt({ visible: false, eventId: null, eventData: null });
  };

  return ( 
    // min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6
      <div
        className={
          themes === "lightMode"
            ? "min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6"
            : "min-h-screen p-6 dashboard-bg"
        }
      >
      <ToastContainer position="top-right" autoClose={3000} theme="light" />

      <div className="mx-auto min-h-screen flex flex-col gap-6">
        {/* Header */}
        <div className="page-heading">
          <div>
            <h3 className="text-3xl font-bold text-gray-800">Welcome back, {user?.firstName}
              {/* {user?.role==='data_entry_operator' && " DEO"}
              {user?.role==='chief_of_staff' && " Chief of Staff"}
              {user?.role==='client' && " Client"} */}
            </h3>
            <p className="text-gray-600 heading-white">Manage your political consultancy operations and track team activities</p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            // className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg transition"
          >
            <Plus className="w-5 h-5" />
            <span>Create Event</span>
          </Button>
        </div>

        {/* Layout */}
        <div className="flex-1 flex gap-6">
          {/* Left Sidebar */}
          <div className="w-80 flex-shrink-0">
            <LeftSidebar 
              onCalendarClick={handleSidebarCalendarClick}
              // onDNDCreated={handleDNDCreated}
              onDNDCreated={refetchDND}
              onScheduleMeetingClick={() => openCreateEventModalWithTab('appointment')}
              teamStatus={data?.data?.team}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col gap-6 ">
            {showCalendarView ? (
              <>
                <CalendarComponent
                  onToggleView={() => setShowCalendarView(false)}
                  onDateSelect={handleDateSelect}
                  initialView={calendarView}
                  selectedDate={selectedDate}
                  events={events}
                  dndEntries={dndEntries}
                  refetchDND={refetchDND}
                  onEventClick={openEventModal}
                />

                {/* Recent Activities */}
               {/* {user?.role!=='data_entry_operator' && <div className="bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activities</h3>
                  <div className="space-y-3">
                    {[
                      { icon: '✅', text: 'Approved community outreach event', time: '2 hours ago', user: 'Sarah Johnson' },
                      { icon: '👤', text: 'New team member added to Campaign', time: '4 hours ago', user: 'Michael Brown' },
                      { icon: '📅', text: 'Updated schedule for candidate', time: '6 hours ago', user: 'Jennifer Adams' },
                      { icon: '📄', text: 'Document review completed', time: '8 hours ago', user: 'David Wilson' },
                      { icon: '⚠️', text: 'Budget alert: Campaign expenses', time: '1 day ago', user: 'System Alert' },
                      { icon: '👥', text: 'Team meeting scheduled', time: '1 day ago', user: 'Sarah Johnson' }
                    ].map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 border border-white/20 shadow-md">
                        <div className="text-lg">{activity.icon}</div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-800">{activity.text}</p>
                          <p className="text-xs text-gray-500 mt-1">{activity.time} • {activity.user}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>} */}
              </>
            ) : (
              <PendingApprovals 
                onToggleView={() => setShowCalendarView(true)} 
                events={events}
                onEventClick={openEventModal}
                setCalendarPrompt={setCalendarPrompt}
              />
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-80 flex-shrink-0">
            {/* <RightSidebar /> */}
            <RightSidebar 
              overview={data?.data?.overview}
              quickStats={data?.data?.stats}
              todaysSchedule={data?.data?.todaysSchedule}
              onEventClick={openEventModal}
              events={events}
            />
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
     <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateEvent={handleCreateEvent}
        selectedDate={selectedDate}
        selectedStartTime={selectedTime}
        selectedEndTime={selectedEndTime}
        dndEntries={dndEntries}
        // onDNDCreated={handleDNDCreated}
        onDNDCreated={refetchDND}
        initialTab={createEventTab} // <-- NEW
      />
      <EventDetailsModal
        isOpen={isEventModalOpen}
        eventId={selectedEventId}
        onClose={closeEventModal}
      />
      {calendarPrompt.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-96 text-center space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Add to Google Calendar?
            </h3>
            <p className="text-sm text-gray-600">
              Do you want to add this {calendarPrompt.eventData?.typeOfEvent || 'event'} to your Google Calendar?
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={confirmCalendarSync}
              >
                Yes
              </button>
              <button
                className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
                onClick={cancelCalendarSync}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
